<template>
  <div :class="$style.productListing">
    <ProductGalleryWrapper
      v-if="data?.fields?.images"
      :images="data?.fields?.images"
      :has-marketing-materials="hasMarketingMaterials"
    />
    <div :class="$style.details">
      <BaseHeading size="h2" :class="$style.header">
        {{ data?.fields?.name }}
      </BaseHeading>
      <div
        v-if="computedReleaseDate || computedEarlyAccessDate"
        :class="$style.releaseDates"
      >
        <div v-if="computedReleaseDate" :class="$style.releaseDate">
          {{ localizedStrings['release-date'] || 'Release Date' }}:
          {{ computedReleaseDate }}
        </div>
        <div v-if="computedEarlyAccessDate" :class="$style.earlyAccessDate">
          <span :class="$style.label">
            {{ localizedStrings['early-access'] || 'WPN Early Access' }}:
          </span>
          {{ computedEarlyAccessDate }}
        </div>
      </div>
      <ProductVariantSelector
        v-if="variants && variants.length > 1"
        :variants="variants"
        :selected-variant="selectedVariant"
        @set-active-variant="setActiveVariant"
      />
      <!-- <MarkedParser
        :md="data?.fields?.type?.fields?.label"
        :class="$style.copy"
      /> -->
      <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />
      <div v-if="computedPackageContents" :class="$style.contents">
        <div :class="$style.contentsHeader">
          {{ localizedStrings['contents'] || 'Contents' }}:
        </div>
        <MarkedParser :md="computedPackageContents" :class="$style.copy" />
      </div>
      <ProductDetails v-if="!isEventPage" :data="data" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseISO } from 'date-fns'
import { formatLocalizedDate } from '../utils/localizeDates'

import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()
const { $visibility } = useNuxtApp()

const emit = defineEmits(['set-active-variant'])

const props = defineProps<{
  data: TypeProductWithoutUnresolvableLinksResponse
  isEventPage?: boolean
  hasMarketingMaterials?: boolean
  variants?: (TypeProductWithoutUnresolvableLinksResponse | undefined)[]
  selectedVariant?: TypeProductWithoutUnresolvableLinksResponse
}>()

// Computed
const computedPackageContents = computed(
  () => props.data?.fields?.archiveCopy || props.data?.fields?.packageContents,
)

const computedReleaseDate = computed(() => {
  const releaseDate = props.data?.fields?.releaseDate?.split('T')?.[0]
  return releaseDate
    ? formatLocalizedDate(parseISO(releaseDate).toString(), 'PP', locale.value)
    : ''
})

const computedEarlyAccessDate = computed(() => {
  const isVisible = $visibility(props.data?.fields?.earlyAccessVisibility)

  const earlyAccessDate = props.data?.fields?.earlyAccess?.split('T')?.[0]
  return isVisible && earlyAccessDate
    ? formatLocalizedDate(
        parseISO(earlyAccessDate).toString(),
        'PP',
        locale.value,
      )
    : ''
})

// Methods
const setActiveVariant = (id: string) => {
  emit('set-active-variant', id)
}
</script>

<style lang="scss" module>
.productListing {
  margin: 0 20px;

  @include media('sm') {
    align-items: flex-start;
    display: flex;
    justify-content: center;
  }
}

.header {
  @include media('sm') {
    margin-bottom: 5px;
  }
}

.releaseDates {
  display: flex;
  flex-wrap: wrap;
  column-gap: 15px;
  margin-top: 1rem;
}

.releaseDate,
.earlyAccessDate {
  color: var(--gray-8);
  font-family: Georgia;
  font-size: to-rem(16px);
  font-style: italic;
  line-height: math.div(28, 16);
  margin-bottom: 10px;

  @include media('sm') {
    font-size: to-rem(19px);
    margin-bottom: 15px;
  }
}

.earlyAccessDate {
  padding: 0 15px;
  border: 1px solid var(--purple);
  border-radius: 9999px;
  font-weight: 700;

  .label {
    color: var(--purple);
  }
}

.copy {
  color: var(--gray-8);
  font-size: to-rem(18px);
  line-height: math.div(29, 18);
  margin-bottom: 20px;

  @include media('sm') {
    font-size: to-rem(19px);
    line-height: math.div(30, 19);
  }

  p + p {
    margin-top: 1rem;
  }

  ul {
    margin-bottom: 2rem;
  }
}

.contents {
  margin-bottom: 32px;

  @include media('sm') {
    margin-bottom: 40px;
  }
}

.contentsHeader {
  font-family: var(--roboto-condensed);
  font-size: to-rem(22px);
  font-weight: bold;
  line-height: math.div(28, 22);
  margin-bottom: 10px;

  @include media('sm') {
    font-size: to-rem(29px);
    line-height: math.div(33, 29);
    margin-bottom: 15px;
  }
}

.contentsList {
  @include list-unstyled();
  font-size: to-rem(18px);
  font-weight: bold;
  line-height: math.div(25, 18);

  @include media('sm') {
    font-size: to-rem(19px);
    line-height: math.div(28, 19);
  }
}

.contentsItem {
  margin-bottom: 5px;
  padding-left: 19px;
  position: relative;

  &:before {
    background-color: var(--purple-light);
    content: '';
    display: block;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
}

.details {
  @include media('sm') {
    flex: 0 1 715px;
  }
}
</style>
